import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMiniChatOptional } from "../../context/MiniChatContext";
import { get } from "../../services/api";
import { isUnifloSystemIdentity } from "../../utils/systemAvatar";
import styles from "./WorkspacePeopleRail.module.css";

/*
 * WorkspacePeopleRail — the right-hand "who's online" rail + invite FAB.
 *
 * Extracted out of pages/clanRedesign/index.jsx (previously private to that
 * 21.5k-line file) so a non-clanRedesign consumer — Clan Admin's
 * AdminLayout.jsx — can mount the exact same, already-data-backed component
 * instead of standing up a second implementation. See
 * .agent/clan-admin-parity-execplan.md for the wider programme this
 * extraction is part of.
 *
 * Behaviour is byte-for-byte unchanged from the pre-extraction version for
 * every existing clanRedesign surface: Home builds its own people list from
 * already-loaded board/member data and renders <RightPeopleRail> directly;
 * Search, Goals, Roles, Subclans, Flows, Reports, Profile, Usage, Support
 * and Bonds render <WorkspacePeopleRail>, which fetches real members itself
 * via GET /api/v1/workspace/members/search. Only the file this code lives
 * in — and the CSS token strategy (see the colocated .module.css) — changed.
 */

// Deterministic hue from an id/string so the same person keeps one colour.
const AVATAR_HUES = ["violet", "teal", "slate", "amber", "blue", "pink"];

function hueFromId(value = "") {
  const str = `${value || ""}`;
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return AVATAR_HUES[hash % AVATAR_HUES.length];
}

function fullName(person = {}) {
  return (
    `${person?.first_name || person?.firstName || ""} ${
      person?.last_name || person?.lastName || ""
    }`.trim() ||
    person?.name ||
    person?.username ||
    person?.email ||
    ""
  );
}

// Return a real, usable photo URL or "". A value is usable only when it is a
// non-empty string AND not one of the known fallback placeholders (the default
// avatar image or the Uniflo brand mark) — those are not real user photos and
// should fall through to the coloured-initials avatar.
function usablePhoto(url) {
  const trimmed = `${url || ""}`.trim();
  if (!trimmed) return "";
  // Platform DEFAULT avatars (brand mark + PWA/touch icons under the brand
  // asset dir) are not real user uploads — treat them as "no photo" so the
  // avatar falls back to colored initials instead of showing the logo or
  // attempting a doomed load (e.g. /uniflo/images/uniflo/touch1.png, 404).
  if (
    trimmed.includes("default_pic") ||
    trimmed.includes("/images/default") ||
    trimmed.includes("/images/uniflo/") ||
    trimmed.includes("uniflo-mark") ||
    trimmed.includes("uniflo-reference")
  ) {
    return "";
  }
  // A real uploaded photo is returned exactly as stored so it loads from
  // wherever it actually lives. (We deliberately do NOT rewrite the host:
  // the app's public origin does not serve every stored asset path, so
  // rewriting could 404 a genuine upload.)
  return trimmed;
}

function workspaceMemberId(member = {}) {
  const id =
    member?.userId ||
    member?.user?._id ||
    member?.user?.id ||
    member?._id ||
    member?.id ||
    member?.guid ||
    "";
  return `${id}`.trim();
}

// Unwrap the standard API envelope ({ status, data: {...} }) returned by
// the shared `get` helper, mirroring how the legacy screens read it.
function unwrapData(response) {
  return response?.data?.data || response?.data || {};
}

function initials(name = "") {
  const parts = `${name || ""}`.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  return `${parts[0][0] || ""}${parts[1]?.[0] || ""}`.toUpperCase();
}

// Only the one glyph this component needs, copied verbatim (same path data)
// from clanRedesign's own ICON_PATHS.plus / Icon so the invite FAB renders
// byte-for-byte the same SVG it always has.
const ICON_PATHS = {
  plus: ["M12 5v14M5 12h14"],
};

function Icon({ name = "plus", size = 16 }) {
  const paths = ICON_PATHS[name] || ICON_PATHS.plus;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {paths.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

function Avatar({
  label = "U",
  hue = "violet",
  lg = false,
  photo,
  title,
  testId,
  allowBrandPhoto = false,
}) {
  const [failed, setFailed] = useState(false);
  const rawPhoto = `${photo || ""}`.trim();
  const src = allowBrandPhoto ? rawPhoto : usablePhoto(photo);
  const showPhoto = src && !failed;
  return (
    <span
      className={[styles.avatar, styles[`avatar_${hue}`], lg ? styles.avatarLg : ""].filter(Boolean).join(" ")}
      data-testid={testId || undefined}
      title={title || label}
      aria-label={title || label}
    >
      {showPhoto ? (
        <img
          src={src}
          className={styles.avatarImg}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        label
      )}
    </span>
  );
}

function peopleRailWithClanLogo(people, { clanName, clanLogoUrl } = {}) {
  const basePeople = Array.isArray(people) ? people.filter(Boolean) : [];
  const safeClanName = `${clanName || "Clan"}`.trim() || "Clan";
  const logoEntry = {
    label: initials(safeClanName) || "CL",
    name: safeClanName,
    hue: "violet",
    photo: `${clanLogoUrl || ""}`.trim(),
    online: true,
    kind: "clan-logo",
    allowBrandPhoto: true,
    ariaLabel: `${safeClanName} clan logo`,
  };
  if (basePeople.some((person) => person?.kind === "clan-logo")) {
    return basePeople;
  }
  if (!basePeople.length) return [logoEntry];
  return [basePeople[0], logoEntry, ...basePeople.slice(1)].filter(Boolean);
}

function normalizeMemberRailResponse(response) {
  const data = unwrapData(response);
  const members = Array.isArray(data?.members)
    ? data.members
    : Array.isArray(data?.users)
      ? data.users
      : [];
  return members
    .map((member) => ({
      ...member,
      name: member?.name || member?.displayName || fullName(member),
      displayName: member?.displayName || member?.name || fullName(member),
    }))
    .filter((member) => workspaceMemberId(member));
}

function memberRailPersonFromMember(member = {}) {
  const username = `${member?.username || ""}`.trim();
  const name = member?.name || member?.displayName || fullName(member) || username || "Member";
  const id = workspaceMemberId(member);
  const isUnifloAi = isUnifloSystemIdentity(member);
  return {
    label: initials(name),
    name,
    username,
    hue: hueFromId(member?._id || member?.membershipId || member?.email || id || name),
    photo: usablePhoto(member?.photo || member?.avatar || ""),
    online: false,
    member: id
      ? {
          _id: id,
          id,
          name,
          displayName: name,
          username: isUnifloAi ? username || "uniflo-ai" : username,
          email: `${member?.email || ""}`.trim(),
          first_name: member?.first_name || member?.firstName || "",
          last_name: member?.last_name || member?.lastName || "",
          photo: member?.photo || member?.avatar || "",
          guid: member?.guid,
          ...(isUnifloAi ? { isSystemAi: true, systemIdentity: "uniflo_ai" } : {}),
        }
      : null,
  };
}

function buildMemberRailPeople(members = [], { limit = 12 } = {}) {
  return (Array.isArray(members) ? members : [])
    .filter((member) => !isUnifloSystemIdentity(member) || workspaceMemberId(member))
    .map(memberRailPersonFromMember)
    .filter((person) => person.member)
    .slice(0, limit);
}

function buildCompactMemberRailPeople(status = "empty") {
  const meta = {
    loading: {
      label: "...",
      name: "Loading members",
      ariaLabel: "Loading clan members",
      hue: "slate",
    },
    unavailable: {
      label: "!",
      name: "Members unavailable",
      ariaLabel: "Clan members are unavailable",
      hue: "amber",
    },
    empty: {
      label: "0",
      name: "No members loaded",
      ariaLabel: "No active clan members loaded",
      hue: "slate",
    },
  }[status] || {
    label: "0",
    name: "No members loaded",
    ariaLabel: "No active clan members loaded",
    hue: "slate",
  };

  return [
    {
      ...meta,
      online: false,
      kind: `member-rail-${status}`,
    },
  ];
}

export function RightPeopleRail({
  people,
  clanName,
  clanLogoUrl,
  onOpenConversation,
  onInvite,
}) {
  const railPeople = useMemo(
    () => peopleRailWithClanLogo(people, { clanName, clanLogoUrl }),
    [clanLogoUrl, clanName, people]
  );
  return (
    <aside className={styles.peopleRail} aria-label="Online members">
      <div className={styles.peopleRailList}>
        {railPeople.map((person, index) => {
          // Clicking a member avatar pops the in-place MiniChat (1:1 DM) for
          // that member. A member is chattable only when it carries a real
          // id (live data); static/compact fallback rail entries have no
          // member object and stay inert.
          const member = person.member;
          const canChat = Boolean(member && (member._id || member.id) && onOpenConversation);
          const isClanLogo = person?.kind === "clan-logo";
          return (
            <button
              key={`${person.kind || "member"}-${person.label}-${person.hue}-${index}`}
              type="button"
              className={styles.peopleRailAvatar}
              aria-label={
                person.ariaLabel ||
                (isClanLogo
                  ? `${person.name || clanName || "Clan"} clan logo`
                  : // Only promise messaging when it is actually reachable —
                    // on routes without MiniChatProvider the button is
                    // disabled, so "Message X" would describe an action the
                    // control cannot perform.
                    `${canChat ? "Message " : ""}${person.name || person.label}${
                      person.online ? " (online)" : ""
                    }`)
              }
              data-testid="clan-redesign-people-rail-avatar"
              disabled={!canChat}
              onClick={() => {
                if (canChat) onOpenConversation(member);
              }}
            >
              <Avatar
                label={person.label}
                hue={person.hue}
                photo={person.photo}
                title={person.ariaLabel || person.name || person.label}
                allowBrandPhoto={person.allowBrandPhoto}
              />
              <span className={[styles.peopleRailDot, person.online ? styles.peopleRailDotOnline : ""].filter(Boolean).join(" ")} />
            </button>
          );
        })}
      </div>
      {/* Opens the in-place InviteDialog over the page. */}
      <button
        type="button"
        className={styles.peopleRailAdd}
        aria-label="Invite member"
        data-testid="clan-redesign-invite-button"
        onClick={onInvite}
      >
        <Icon name="plus" size={20} />
      </button>
    </aside>
  );
}

// Member rail for non-Home surfaces: real clan members when the lightweight
// workspace search endpoint is available, explicit compact state otherwise.
export default function WorkspacePeopleRail({
  clanName,
  clanLogoUrl,
  tenantSlug = "",
  workspaceKind = "tenant",
  subclanId = "",
  user,
  onInvite,
}) {
  // Optional on purpose. This rail renders on the clan workspace (inside
  // MiniChatProvider) and on the clan admin console, which is not. Using the
  // throwing useMiniChat() here took every /administrator page down with
  // "useMiniChat must be used within MiniChatProvider". When chat is
  // unavailable the avatars still show presence but their buttons render
  // disabled, rather than looking clickable and doing nothing.
  const miniChat = useMiniChatOptional();
  const openMiniChat = miniChat?.openMiniChat || null;
  const resolvedSubclanId = `${subclanId || ""}`.trim();
  const isSubclanWorkspace = workspaceKind === "subclan" && Boolean(resolvedSubclanId);
  const canLoadMembers = Boolean(user && (!workspaceKind || workspaceKind !== "subclan" || resolvedSubclanId));
  const memberRailQuery = useQuery({
    queryKey: [
      "clan-redesign-member-rail",
      tenantSlug || "custom-domain",
      isSubclanWorkspace ? `subclan:${resolvedSubclanId}` : "tenant",
    ],
    queryFn: () => {
      const params = isSubclanWorkspace
        ? `?q=&subclanId=${encodeURIComponent(resolvedSubclanId)}`
        : "?q=";
      return get(`/api/v1/workspace/members/search${params}`);
    },
    enabled: canLoadMembers,
    staleTime: 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const memberRailMembers = useMemo(
    () => normalizeMemberRailResponse(memberRailQuery.data),
    [memberRailQuery.data]
  );
  const memberRailPeople = useMemo(() => {
    const livePeople = buildMemberRailPeople(memberRailMembers);
    if (livePeople.length) return livePeople;
    if (!canLoadMembers || memberRailQuery.isLoading || memberRailQuery.isFetching) {
      return buildCompactMemberRailPeople("loading");
    }
    if (memberRailQuery.isError) {
      return buildCompactMemberRailPeople("unavailable");
    }
    return buildCompactMemberRailPeople("empty");
  }, [
    canLoadMembers,
    memberRailMembers,
    memberRailQuery.isError,
    memberRailQuery.isFetching,
    memberRailQuery.isLoading,
  ]);
  const openConversation = useCallback(
    (member) => {
      if (!member || !(member._id || member.id)) return;
      openMiniChat(member);
    },
    [openMiniChat]
  );

  return (
    <RightPeopleRail
      people={memberRailPeople}
      clanName={clanName}
      clanLogoUrl={clanLogoUrl}
      onOpenConversation={openConversation}
      onInvite={onInvite}
    />
  );
}
